import React, { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';

import { TEACHERS_PERMISSION_DENIED_URL } from 'config/urls';
import { isEmpty, isNil } from 'lodash';
import {
  classroomInsightsWarmup,
  useLiveClassroomStudentData,
  useTrackerInstanceSectionClassroomInsightsStatus,
  useTrackerInstanceSectionDetail
} from 'sdk';

import { LIVE_CLASSROOM_TEACHERS_DOMAIN } from 'utils/constants';

import ClassroomDashboard from './components/ClassroomDashboard/index';
import ClassroomHeader from './components/ClassroomHeader/index';
import ClassroomStatuses from './components/ClassroomStatuses/index';
import ClassroomStatusesSkeleton from './components/ClassroomStatusesSkeleton/index';
import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const LiveClassroom = () => {
  const { user } = useContext(UserContext);
  const history = useHistory();

  const { trackerInstanceSectionId } = useParams();

  const { data: trackerInstanceSectionDetail } =
    useTrackerInstanceSectionDetail({
      trackerInstanceSectionId
    });

  const { data: studentData, refetch: refetchStudentData } =
    useLiveClassroomStudentData({
      trackerInstanceSectionId
    });

  const { data: insightsStatus, refetch: refetchInsightsStatus } =
    useTrackerInstanceSectionClassroomInsightsStatus({
      trackerInstanceSectionId
    });

  useEffect(() => {
    const interval = setInterval(() => {
      refetchStudentData();
      refetchInsightsStatus();
    }, 15000);

    return () => clearInterval(interval);
  });

  useEffect(() => {
    classroomInsightsWarmup();
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (
      !user.is_live_classroom_teacher &&
      !LIVE_CLASSROOM_TEACHERS_DOMAIN.includes(user.email.split('@')[1])
    ) {
      history.push(TEACHERS_PERMISSION_DENIED_URL);
    }
  }, [user, history]);

  const students = useMemo(() => {
    if (isNil(studentData)) return null;
    if (isEmpty(studentData)) return [];

    return Object.entries(studentData)
      .map(([key, item], _) =>
        item.map((student) => ({ ...student, status: key }))
      )
      .flat();
  }, [studentData]);

  return (
    <div className={styles.container}>
      <ClassroomHeader
        sectionDetails={trackerInstanceSectionDetail}
        studentsCount={isNil(students) ? 0 : students.length}
      />
      {isNil(studentData) && <ClassroomStatusesSkeleton />}
      {!isNil(studentData) && <ClassroomStatuses studentData={studentData} />}
      <ClassroomDashboard students={students} insightsStatus={insightsStatus} />
    </div>
  );
};

export default LiveClassroom;
